// Theme is stored per browser (RD §3.8), never in the DB. public/theme-init.js applies the
// saved choice before first paint; this provider keeps it in sync afterwards.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

export type ThemeChoice = "light" | "dark" | "system";

export const THEME_STORAGE_KEY = "tt-theme";
const DARK_QUERY = "(prefers-color-scheme: dark)";
/** Dark is the default (original app; architecture sprint-02 §11.1). Keep in sync with public/theme-init.js. */
export const DEFAULT_THEME: ThemeChoice = "dark";
/** Sprint 18 (owner): the public report page keeps its own choice and defaults to light. Keep in sync with public/theme-init.js. */
export const PUBLIC_THEME_STORAGE_KEY = "tt-public-theme";
export const PUBLIC_DEFAULT_THEME: ThemeChoice = "light";

interface ThemeContextValue {
  theme: ThemeChoice;
  setTheme: (theme: ThemeChoice) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

function isThemeChoice(value: unknown): value is ThemeChoice {
  return value === "light" || value === "dark" || value === "system";
}

function readStoredTheme(key: string, fallback: ThemeChoice): ThemeChoice {
  try {
    const stored = window.localStorage.getItem(key);
    return isThemeChoice(stored) ? stored : fallback;
  } catch {
    return fallback;
  }
}

function writeStoredTheme(key: string, theme: ThemeChoice): void {
  try {
    window.localStorage.setItem(key, theme);
  } catch {
    // Storage blocked (private mode, policy): the choice simply lasts for this page view.
  }
}

function systemPrefersDark(): boolean {
  try {
    return window.matchMedia(DARK_QUERY).matches;
  } catch {
    return false;
  }
}

function applyTheme(theme: ThemeChoice): void {
  const dark = theme === "dark" || (theme === "system" && systemPrefersDark());
  document.documentElement.classList.toggle("dark", dark);
}

/**
 * The app-wide provider uses the defaults. The public report page nests a second one with its own storage
 * key and a light default; when it unmounts (e.g. the viewer signs in) the app's own choice is re-applied.
 */
export function ThemeProvider({
  children,
  storageKey = THEME_STORAGE_KEY,
  defaultTheme = DEFAULT_THEME,
}: {
  children: ReactNode;
  storageKey?: string;
  defaultTheme?: ThemeChoice;
}): JSX.Element {
  const [theme, setThemeState] = useState<ThemeChoice>(() =>
    readStoredTheme(storageKey, defaultTheme),
  );

  useEffect(() => {
    if (storageKey === THEME_STORAGE_KEY) return undefined;
    return () => applyTheme(readStoredTheme(THEME_STORAGE_KEY, DEFAULT_THEME));
  }, [storageKey]);

  useEffect(() => {
    applyTheme(theme);
    if (theme !== "system") return undefined;
    let media: MediaQueryList;
    try {
      media = window.matchMedia(DARK_QUERY);
    } catch {
      return undefined;
    }
    const onChange = (): void => applyTheme("system");
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [theme]);

  const setTheme = useCallback(
    (next: ThemeChoice) => {
      writeStoredTheme(storageKey, next);
      setThemeState(next);
    },
    [storageKey],
  );

  const value = useMemo(() => ({ theme, setTheme }), [theme, setTheme]);
  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used inside <ThemeProvider>");
  return ctx;
}
